import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/AuthContext";
import { PageHeader, DataTable } from "@/components/PageHeader";

const HEAD = ["Email", "Registro", "Estado", "Acción"];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("es-ES") : "—");

const StatusPill = ({ activo }) => (
  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${activo ? "bg-emerald-500/15 text-emerald-400" : "bg-[var(--gold-soft)] text-[var(--gold)]"}`}>
    {activo ? "Activa" : "Pendiente"}
  </span>
);

const ToggleButton = ({ p, self, busy, onToggle, testid }) => (self ? <span className="text-xs text-zinc-500">Tu cuenta</span> : (
  <button data-testid={testid} disabled={busy} onClick={() => onToggle(p)}
    className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-60 ${p.activo ? "border border-white/10 text-zinc-300 hover:border-red-400 hover:text-red-400" : "bg-[var(--gold)] text-black hover:bg-amber-400"}`}>
    {busy && <Loader2 className="h-3 w-3 animate-spin" />}{p.activo ? "Desactivar" : "Activar"}
  </button>
));

const MobileCard = ({ p, self, busy, onToggle }) => (
  <div data-testid={`account-card-${p.id}`} className="rounded-xl border border-white/5 bg-[#111114] p-4">
    <p className="truncate font-semibold">{p.email || "—"}</p>
    <p className="mt-1 text-xs text-zinc-500">Registro: {fmt(p.creado)}</p>
    <div className="mt-3 flex items-center justify-between">
      <StatusPill activo={p.activo} />
      <ToggleButton p={p} self={self} busy={busy} onToggle={onToggle} testid={`account-card-toggle-${p.id}`} />
    </div>
  </div>
);

const Row = ({ p, self, busy, onToggle }) => (
  <tr data-testid={`account-row-${p.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
    <td className="px-5 py-3 font-semibold">{p.email || "—"}{p.es_admin && <ShieldCheck className="ml-2 inline h-4 w-4 text-[var(--gold)]" />}</td>
    <td className="px-5 py-3 text-zinc-400">{fmt(p.creado)}</td>
    <td className="px-5 py-3"><StatusPill activo={p.activo} /></td>
    <td className="px-5 py-3">
      <ToggleButton p={p} self={self} busy={busy} onToggle={onToggle} testid={`account-toggle-${p.id}`} />
    </td>
  </tr>
);

export default function AdminPage() {
  const { session } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc("listar_perfiles");
    setLoading(false);
    if (error) { toast.error("No se pudo cargar el listado", { description: error.message }); return; }
    setList(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    setBusyId(p.id);
    const { error } = await supabase.rpc("activar_usuario", { p_id: p.id, p_activo: !p.activo });
    setBusyId(null);
    if (error) { toast.error("No se pudo actualizar la cuenta", { description: error.message }); return; }
    toast.success(p.activo ? "Cuenta desactivada" : "Cuenta activada");
    load();
  };

  const pending = list.filter((p) => !p.activo).length;
  const rows = list.map((p) => (
    <Row key={p.id} p={p} self={p.id === session?.user?.id} busy={busyId === p.id} onToggle={toggle} />
  ));
  const subtitle = loading ? "Cargando cuentas…" : `${pending} ${pending === 1 ? "cuenta pendiente" : "cuentas pendientes"} de activación`;
  return (
    <>
      <PageHeader title="Activaciones" subtitle={subtitle} />
      <div className="space-y-3 md:hidden" data-testid="accounts-cards">
        {list.map((p) => (
          <MobileCard key={p.id} p={p} self={p.id === session?.user?.id} busy={busyId === p.id} onToggle={toggle} />
        ))}
      </div>
      <div className="hidden md:block">
        <DataTable testid="accounts-table" head={HEAD} rows={rows} empty={loading ? "Cargando…" : "No hay cuentas registradas"} />
      </div>
    </>
  );
}
